/**
 * SignLanguageAvatarLive - Professional 3D-style animated avatar for sign language interpretation
 * Features:
 * - Realistic 3D-style avatar with smooth hand movements
 * - Professional appearance with detailed facial features
 * - 26+ sign languages support including 8 African regional languages
 * - Customizable appearance (skin tone, clothing, hair, accessories)
 * - Smooth CSS animations with expression changes
 * - Enhanced gesture library for fingerspelling
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Maximize2, Minimize2, X, Hand, Volume2, Sparkles } from 'lucide-react';

// Comprehensive sign language configurations - 26+ languages
export const SIGN_LANGUAGE_CONFIG = {
  // Americas (4)
  asl: { name: 'ASL', fullName: 'American Sign Language', region: 'USA/Canada', flag: '🇺🇸', family: 'French', users: '500K+' },
  lsm: { name: 'LSM', fullName: 'Mexican Sign Language', region: 'Mexico', flag: '🇲🇽', family: 'French', users: '100K+' },
  libras: { name: 'Libras', fullName: 'Brazilian Sign Language', region: 'Brazil', flag: '🇧🇷', family: 'French', users: '3M+' },
  lsa: { name: 'LSA', fullName: 'Argentine Sign Language', region: 'Argentina', flag: '🇦🇷', family: 'Spanish', users: '100K+' },
  
  // Europe (7)
  bsl: { name: 'BSL', fullName: 'British Sign Language', region: 'UK', flag: '🇬🇧', family: 'BANZSL', users: '150K+' },
  lsf: { name: 'LSF', fullName: 'French Sign Language', region: 'France', flag: '🇫🇷', family: 'French', users: '100K+' },
  dgs: { name: 'DGS', fullName: 'German Sign Language', region: 'Germany', flag: '🇩🇪', family: 'German', users: '200K+' },
  lis: { name: 'LIS', fullName: 'Italian Sign Language', region: 'Italy', flag: '🇮🇹', family: 'French', users: '40K+' },
  lse: { name: 'LSE', fullName: 'Spanish Sign Language', region: 'Spain', flag: '🇪🇸', family: 'Spanish', users: '100K+' },
  pjm: { name: 'PJM', fullName: 'Polish Sign Language', region: 'Poland', flag: '🇵🇱', family: 'German', users: '50K+' },
  rsl: { name: 'RSL', fullName: 'Russian Sign Language', region: 'Russia', flag: '🇷🇺', family: 'Russian', users: '120K+' },
  
  // Africa (8)
  sasl: { name: 'SASL', fullName: 'South African Sign Language', region: 'South Africa', flag: '🇿🇦', family: 'BANZSL', users: '600K+' },
  zsl: { name: 'ZSL', fullName: 'Zimbabwe Sign Language', region: 'Zimbabwe', flag: '🇿🇼', family: 'African', users: '200K+' },
  ksl: { name: 'KSL', fullName: 'Kenyan Sign Language', region: 'Kenya', flag: '🇰🇪', family: 'African', users: '150K+' },
  ugsl: { name: 'UGSL', fullName: 'Ugandan Sign Language', region: 'Uganda', flag: '🇺🇬', family: 'African', users: '100K+' },
  tsl: { name: 'TSL', fullName: 'Tanzanian Sign Language', region: 'Tanzania', flag: '🇹🇿', family: 'African', users: '80K+' },
  gsl: { name: 'GSL', fullName: 'Ghanaian Sign Language', region: 'Ghana', flag: '🇬🇭', family: 'African', users: '50K+' },
  nsl: { name: 'NSL', fullName: 'Nigerian Sign Language', region: 'Nigeria', flag: '🇳🇬', family: 'African', users: '300K+' },
  esl: { name: 'ESL', fullName: 'Ethiopian Sign Language', region: 'Ethiopia', flag: '🇪🇹', family: 'African', users: '100K+' },
  
  // Asia-Pacific (6)
  auslan: { name: 'Auslan', fullName: 'Australian Sign Language', region: 'Australia', flag: '🇦🇺', family: 'BANZSL', users: '20K+' },
  nzsl: { name: 'NZSL', fullName: 'New Zealand Sign Language', region: 'New Zealand', flag: '🇳🇿', family: 'BANZSL', users: '25K+' },
  jsl: { name: 'JSL', fullName: 'Japanese Sign Language', region: 'Japan', flag: '🇯🇵', family: 'Japanese', users: '320K+' },
  csl: { name: 'CSL', fullName: 'Chinese Sign Language', region: 'China', flag: '🇨🇳', family: 'Chinese', users: '20M+' },
  isl_india: { name: 'ISL', fullName: 'Indian Sign Language', region: 'India', flag: '🇮🇳', family: 'Indo-Pakistani', users: '6M+' },
  hksl: { name: 'HKSL', fullName: 'Hong Kong Sign Language', region: 'Hong Kong', flag: '🇭🇰', family: 'Chinese', users: '9K+' },
  
  // International (1)
  is: { name: 'IS', fullName: 'International Sign', region: 'International', flag: '🌍', family: 'International', users: 'Global' },
};

// Skin tone options with realistic gradients
export const SKIN_TONES = {
  'light': { main: '#FFE4C9', shadow: '#E5C4A8', highlight: '#FFF5ED', midtone: '#F5D5BA' },
  'fair': { main: '#F5D0B8', shadow: '#D4AE96', highlight: '#FFEEE4', midtone: '#EAC4AC' },
  'medium-light': { main: '#E0B08A', shadow: '#C4946E', highlight: '#F0CCA8', midtone: '#D5A37C' },
  'medium': { main: '#C68642', shadow: '#A66B30', highlight: '#D8A060', midtone: '#B87838' },
  'olive': { main: '#B07040', shadow: '#8B5530', highlight: '#C88858', midtone: '#A06438' },
  'tan': { main: '#9A6B4A', shadow: '#7A5038', highlight: '#B08060', midtone: '#8A5D40' },
  'medium-dark': { main: '#8D5524', shadow: '#6B4018', highlight: '#A06830', midtone: '#7D481E' },
  'dark': { main: '#5C3317', shadow: '#3D220F', highlight: '#704020', midtone: '#4E2A13' },
  'deep': { main: '#3D2010', shadow: '#281508', highlight: '#4D2818', midtone: '#35190C' },
};

// Hair styles and colors
export const HAIR_COLORS = {
  'black': '#1a1a1a',
  'dark-brown': '#3b2417',
  'brown': '#5c4033',
  'light-brown': '#8b6b4f',
  'auburn': '#922724',
  'red': '#b55239',
  'ginger': '#d4652f',
  'strawberry': '#cc8855',
  'blonde': '#c9a86c',
  'platinum': '#e8dcc8',
  'gray': '#808080',
  'silver': '#b0b0b0',
  'white': '#e8e8e8',
};

// Professional 3D-style hand with detailed finger articulation
const Professional3DHand = ({ isLeft, phase, skinTone, size = 1 }) => {
  const skin = SKIN_TONES[skinTone] || SKIN_TONES.medium;
  
  // Enhanced hand position states with more gestures
  const handStates = {
    rest: { 
      x: isLeft ? -58 : 58, 
      y: 52, 
      rotate: isLeft ? -8 : 8,
      fingers: [0.55, 0.65, 0.7, 0.65, 0.55],
      thumb: 0.75,
      wristRotate: 0,
      palmRotate: 0,
      zIndex: 10,
    },
    wave: { 
      x: isLeft ? -45 : 45, 
      y: 22, 
      rotate: isLeft ? -20 : 20,
      fingers: [1, 0.95, 0.9, 0.95, 1],
      thumb: 0.95,
      wristRotate: isLeft ? -12 : 12,
      palmRotate: isLeft ? -5 : 5,
      zIndex: 20,
    },
    wave_high: { 
      x: isLeft ? -40 : 40, 
      y: 15, 
      rotate: isLeft ? -25 : 25,
      fingers: [1.1, 1, 0.95, 1, 1.1],
      thumb: 1,
      wristRotate: isLeft ? -18 : 18,
      palmRotate: isLeft ? -8 : 8,
      zIndex: 25,
    },
    point: { 
      x: isLeft ? -32 : 32, 
      y: 18, 
      rotate: 0,
      fingers: [0.28, 1.15, 0.28, 0.28, 0.28],
      thumb: 0.45,
      wristRotate: 0,
      palmRotate: 0,
      zIndex: 20,
    },
    open: { 
      x: isLeft ? -55 : 55, 
      y: 28, 
      rotate: isLeft ? -18 : 18,
      fingers: [1.1, 1.12, 1.12, 1.1, 1.05],
      thumb: 1.1,
      wristRotate: isLeft ? -8 : 8,
      palmRotate: 0,
      zIndex: 18,
    },
    open_wide: { 
      x: isLeft ? -62 : 62, 
      y: 25, 
      rotate: isLeft ? -22 : 22,
      fingers: [1.15, 1.18, 1.18, 1.15, 1.1],
      thumb: 1.15,
      wristRotate: isLeft ? -12 : 12,
      palmRotate: isLeft ? -5 : 5,
      zIndex: 22,
    },
    fist: { 
      x: isLeft ? -48 : 48, 
      y: 38, 
      rotate: isLeft ? -12 : 12,
      fingers: [0.32, 0.32, 0.32, 0.32, 0.32],
      thumb: 0.38,
      wristRotate: 0,
      palmRotate: 0,
      zIndex: 15,
    },
    flat: { 
      x: isLeft ? -42 : 42, 
      y: 22, 
      rotate: isLeft ? -28 : 28,
      fingers: [0.92, 0.96, 1, 0.96, 0.92],
      thumb: 0.68,
      wristRotate: isLeft ? -22 : 22,
      palmRotate: isLeft ? -10 : 10,
      zIndex: 20,
    },
    cup: { 
      x: isLeft ? -52 : 52, 
      y: 32, 
      rotate: isLeft ? -10 : 10,
      fingers: [0.78, 0.82, 0.85, 0.82, 0.78],
      thumb: 0.88,
      wristRotate: isLeft ? -5 : 5,
      palmRotate: 0,
      zIndex: 16,
    },
    thumbs_up: { 
      x: isLeft ? -48 : 48, 
      y: 18, 
      rotate: 0,
      fingers: [0.28, 0.28, 0.28, 0.28, 0.28],
      thumb: 1.15,
      wristRotate: isLeft ? 85 : -85,
      palmRotate: 0,
      zIndex: 22,
    },
    ok_sign: { 
      x: isLeft ? -45 : 45, 
      y: 25, 
      rotate: isLeft ? -10 : 10,
      fingers: [0.48, 0.48, 1.05, 1.05, 1.02],
      thumb: 0.48,
      wristRotate: 0,
      palmRotate: 0,
      zIndex: 18,
    },
    peace: { 
      x: isLeft ? -45 : 45, 
      y: 15, 
      rotate: isLeft ? -5 : 5,
      fingers: [0.28, 1.08, 1.08, 0.28, 0.28],
      thumb: 0.38,
      wristRotate: 0,
      palmRotate: 0,
      zIndex: 22,
    },
    love: { 
      x: isLeft ? -45 : 45, 
      y: 15, 
      rotate: isLeft ? -5 : 5,
      fingers: [1.05, 0.28, 0.28, 0.28, 1.05],
      thumb: 1.08,
      wristRotate: 0,
      palmRotate: 0,
      zIndex: 22,
    },
    // Fingerspelling letters
    letter_a: { 
      x: isLeft ? -45 : 45, 
      y: 20, 
      rotate: 0,
      fingers: [0.28, 0.28, 0.28, 0.28, 0.28],
      thumb: 0.92,
      wristRotate: 0,
      palmRotate: 0,
      zIndex: 20,
    },
    letter_b: { 
      x: isLeft ? -45 : 45, 
      y: 15, 
      rotate: 0,
      fingers: [1.05, 1.05, 1.05, 1.05, 0.28],
      thumb: 0.28,
      wristRotate: 0,
      palmRotate: 0,
      zIndex: 22,
    },
    letter_c: { 
      x: isLeft ? -45 : 45, 
      y: 22, 
      rotate: isLeft ? -15 : 15,
      fingers: [0.6, 0.62, 0.64, 0.62, 0.6],
      thumb: 0.62,
      wristRotate: isLeft ? 15 : -15,
      palmRotate: isLeft ? 5 : -5,
      zIndex: 18,
    },
    letter_d: { 
      x: isLeft ? -45 : 45, 
      y: 18, 
      rotate: 0,
      fingers: [0.28, 1.1, 0.28, 0.28, 0.28],
      thumb: 0.55,
      wristRotate: 0,
      palmRotate: 0,
      zIndex: 20,
    },
    clap_in: { 
      x: isLeft ? -25 : 25, 
      y: 35, 
      rotate: isLeft ? 5 : -5,
      fingers: [0.9, 0.95, 0.95, 0.95, 0.9],
      thumb: 0.8,
      wristRotate: isLeft ? 10 : -10,
      palmRotate: isLeft ? 15 : -15,
      zIndex: 20,
    },
    clap_out: { 
      x: isLeft ? -55 : 55, 
      y: 32, 
      rotate: isLeft ? -15 : 15,
      fingers: [0.95, 1, 1, 1, 0.95],
      thumb: 0.85,
      wristRotate: isLeft ? -10 : 10,
      palmRotate: isLeft ? -8 : 8,
      zIndex: 18,
    },
    explain: { 
      x: isLeft ? -50 : 50, 
      y: 28, 
      rotate: isLeft ? -12 : 12,
      fingers: [0.85, 0.9, 0.92, 0.9, 0.85],
      thumb: 0.82,
      wristRotate: isLeft ? -8 : 8,
      palmRotate: isLeft ? -5 : 5,
      zIndex: 17,
    },
  };

  const state = handStates[phase] || handStates.rest;
  const baseSize = 34 * size;

  return (
    <div
      className="absolute transition-all duration-450 ease-out"
      style={{
        left: `calc(50% + ${state.x * size}px)`,
        top: `${state.y}%`,
        transform: `rotate(${state.rotate}deg)`,
        zIndex: state.zIndex,
      }}
    >
      {/* Forearm with gradient */}
      <div
        className="absolute rounded-full transition-all duration-450"
        style={{
          width: `${baseSize * 0.38}px`,
          height: `${baseSize * 2.2}px`,
          background: `linear-gradient(180deg, ${skin.main} 0%, ${skin.midtone} 50%, ${skin.shadow} 100%)`,
          top: `${baseSize * 0.85}px`,
          left: `${baseSize * 0.32}px`,
          transformOrigin: 'top center',
          transform: `rotate(${state.wristRotate}deg)`,
          boxShadow: `
            inset ${isLeft ? '-3px' : '3px'} 0 8px ${skin.shadow}40,
            2px 4px 8px rgba(0,0,0,0.15)
          `,
          borderRadius: '40%',
        }}
      />

      {/* Palm with 3D depth */}
      <div
        className="relative rounded-xl transition-all duration-400"
        style={{
          width: `${baseSize}px`,
          height: `${baseSize * 1.18}px`,
          background: `radial-gradient(ellipse at 35% 35%, ${skin.highlight} 0%, ${skin.main} 35%, ${skin.midtone} 70%, ${skin.shadow} 100%)`,
          boxShadow: `
            inset 3px 3px 10px ${skin.highlight}80,
            inset -3px -3px 10px ${skin.shadow}60,
            3px 5px 12px rgba(0,0,0,0.2),
            -1px -1px 4px rgba(255,255,255,0.1)
          `,
          transform: `rotateY(${state.palmRotate}deg)`,
        }}
      >
        {/* Palm lines for realism */}
        <svg className="absolute inset-0 w-full h-full opacity-25" viewBox="0 0 100 118">
          <path d="M18,38 Q50,32 82,42" stroke={skin.shadow} fill="none" strokeWidth="1.2" strokeLinecap="round"/>
          <path d="M15,58 Q48,52 78,62" stroke={skin.shadow} fill="none" strokeWidth="1" strokeLinecap="round"/>
          <path d="M22,78 Q52,72 72,82" stroke={skin.shadow} fill="none" strokeWidth="0.8" strokeLinecap="round"/>
          <path d="M50,20 Q52,50 50,90" stroke={skin.shadow} fill="none" strokeWidth="0.6" strokeLinecap="round" opacity="0.5"/>
        </svg>

        {/* Thumb with detailed joints */}
        <div
          className="absolute transition-all duration-400"
          style={{
            width: `${baseSize * 0.28}px`,
            height: `${baseSize * 0.58 * state.thumb}px`,
            background: `linear-gradient(${isLeft ? '50deg' : '-50deg'}, ${skin.highlight} 0%, ${skin.main} 40%, ${skin.shadow} 100%)`,
            borderRadius: `${baseSize * 0.14}px`,
            left: isLeft ? `${baseSize * 0.88}px` : `-${baseSize * 0.12}px`,
            top: `${baseSize * 0.38}px`,
            transformOrigin: isLeft ? 'left center' : 'right center',
            transform: `rotate(${isLeft ? -42 : 42}deg)`,
            boxShadow: `
              inset 1px 1px 4px ${skin.highlight}60,
              2px 2px 5px rgba(0,0,0,0.12)
            `,
          }}
        >
          {/* Thumb nail */}
          <div
            className="absolute rounded-t-sm"
            style={{
              width: `${baseSize * 0.18}px`,
              height: `${baseSize * 0.1}px`,
              background: 'linear-gradient(180deg, #ffe8e0 0%, #ffd4cc 50%, #ffccc0 100%)',
              left: `${baseSize * 0.05}px`,
              top: `${baseSize * 0.03}px`,
              boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.6)',
            }}
          />
        </div>

        {/* Fingers with enhanced detail */}
        {[0, 1, 2, 3, 4].map((finger) => {
          const fingerWidth = baseSize * (finger === 2 ? 0.24 : finger === 1 || finger === 3 ? 0.22 : 0.2);
          const baseHeight = baseSize * (0.52 + (finger === 2 ? 0.14 : finger === 1 || finger === 3 ? 0.08 : 0));
          const fingerHeight = baseHeight * state.fingers[finger];
          const fingerLeft = baseSize * (0.06 + finger * 0.19);
          const fingerSpread = (finger - 2) * (state.fingers[finger] > 0.8 ? 5 : 2.5);

          return (
            <div
              key={finger}
              className="absolute transition-all duration-400"
              style={{
                width: `${fingerWidth}px`,
                height: `${fingerHeight}px`,
                background: `linear-gradient(180deg, ${skin.highlight} 0%, ${skin.main} 25%, ${skin.midtone} 70%, ${skin.shadow} 100%)`,
                borderRadius: `${fingerWidth * 0.5}px ${fingerWidth * 0.5}px ${fingerWidth * 0.35}px ${fingerWidth * 0.35}px`,
                left: `${fingerLeft}px`,
                top: `-${fingerHeight * 0.72}px`,
                transformOrigin: 'bottom center',
                transform: `rotate(${fingerSpread}deg)`,
                boxShadow: `
                  inset 2px 2px 4px ${skin.highlight}70,
                  inset -1px -1px 3px ${skin.shadow}40,
                  1px 3px 6px rgba(0,0,0,0.12)
                `,
              }}
            >
              {/* Fingernail with shine */}
              <div
                className="absolute rounded-t-sm overflow-hidden"
                style={{
                  width: `${fingerWidth * 0.72}px`,
                  height: `${fingerWidth * 0.38}px`,
                  background: 'linear-gradient(180deg, #fff0eb 0%, #ffe8e0 30%, #ffd8d0 100%)',
                  left: `${fingerWidth * 0.14}px`,
                  top: `${fingerWidth * 0.12}px`,
                  boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.7), 0 1px 1px rgba(0,0,0,0.05)',
                }}
              >
                {/* Nail shine */}
                <div
                  className="absolute top-0 left-0 w-1/2 h-full"
                  style={{
                    background: 'linear-gradient(90deg, rgba(255,255,255,0.4) 0%, transparent 100%)',
                  }}
                />
              </div>
              {/* Finger joints */}
              <div
                className="absolute w-full opacity-35"
                style={{
                  height: '1.5px',
                  background: `linear-gradient(90deg, transparent 10%, ${skin.shadow} 50%, transparent 90%)`,
                  top: '32%',
                }}
              />
              <div
                className="absolute w-full opacity-25"
                style={{
                  height: '1px',
                  background: `linear-gradient(90deg, transparent 15%, ${skin.shadow} 50%, transparent 85%)`,
                  top: '58%',
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Professional avatar body with clothing, shoulders, and expressions
const Professional3DBody = ({ skinTone, clothingColor, hairColor, phase, isSigning, expression }) => {
  const skin = SKIN_TONES[skinTone] || SKIN_TONES.medium;
  const hair = HAIR_COLORS[hairColor] || HAIR_COLORS['dark-brown'];
  const [breathePhase, setBreathPhase] = useState(0);
  const [blinkPhase, setBlinkPhase] = useState(0);
  const [microExpression, setMicroExpression] = useState(0);

  // Subtle breathing animation
  useEffect(() => {
    const breatheInterval = setInterval(() => {
      setBreathPhase(p => (p + 1) % 120);
    }, 50);
    return () => clearInterval(breatheInterval);
  }, []);

  // Blink animation with variation
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setBlinkPhase(1);
      setTimeout(() => setBlinkPhase(0), 120);
    }, 2500 + Math.random() * 3000);
    return () => clearInterval(blinkInterval);
  }, []);

  // Micro expressions during signing
  useEffect(() => {
    if (isSigning) {
      const microInterval = setInterval(() => {
        setMicroExpression(Math.random());
      }, 800);
      return () => clearInterval(microInterval);
    }
  }, [isSigning]);

  const breatheOffset = Math.sin(breathePhase * 0.052) * 1.8;
  const shoulderOffset = Math.sin(breathePhase * 0.052 + 0.5) * 1;
  
  // Dynamic shoulder movement based on signing phase
  const leftShoulderLift = isSigning && ['wave', 'wave_high', 'open', 'open_wide', 'flat', 'peace', 'love', 'letter_b'].includes(phase) ? -4 : 0;
  const rightShoulderLift = isSigning && ['point', 'thumbs_up', 'ok_sign', 'letter_d'].includes(phase) ? -4 : 0;

  // Expression states with more nuance
  const expressions = {
    neutral: { eyebrow: 0, mouth: 1, eyeScale: 1, headTilt: 0, eyeDirection: 0, mouthCurve: 0 },
    attentive: { eyebrow: -2, mouth: 1.05, eyeScale: 1.08, headTilt: -2, eyeDirection: 0, mouthCurve: 0.5 },
    emphasis: { eyebrow: -4, mouth: 1.18, eyeScale: 1.12, headTilt: -4, eyeDirection: 0, mouthCurve: 1 },
    question: { eyebrow: 4, mouth: 0.9, eyeScale: 1.18, headTilt: 6, eyeDirection: 2, mouthCurve: -0.5 },
    happy: { eyebrow: 2, mouth: 1.35, eyeScale: 0.92, headTilt: 0, eyeDirection: 0, mouthCurve: 2 },
    surprised: { eyebrow: 5, mouth: 1.45, eyeScale: 1.28, headTilt: 2, eyeDirection: 0, mouthCurve: 0 },
    thinking: { eyebrow: -2, mouth: 0.92, eyeScale: 1.05, headTilt: 8, eyeDirection: 4, mouthCurve: -0.3 },
    explaining: { eyebrow: -1, mouth: 1.12, eyeScale: 1.08, headTilt: -5, eyeDirection: -1, mouthCurve: 0.8 },
    agreeing: { eyebrow: 2, mouth: 1.22, eyeScale: 0.98, headTilt: 4, eyeDirection: 0, mouthCurve: 1.5 },
    focused: { eyebrow: -3, mouth: 0.95, eyeScale: 1.1, headTilt: 0, eyeDirection: 0, mouthCurve: 0 },
  };

  const expr = expressions[expression] || expressions.neutral;
  const isBlinking = blinkPhase > 0;
  
  // Add micro-expression variation
  const microOffset = isSigning ? (microExpression - 0.5) * 2 : 0;

  return (
    <div className="relative w-full h-full flex items-end justify-center overflow-visible">
      {/* Ambient glow when signing */}
      <div 
        className={`absolute inset-0 rounded-xl transition-all duration-700 ${isSigning ? 'opacity-100' : 'opacity-0'}`}
        style={{
          background: `radial-gradient(ellipse at center 55%, ${adjustColor(clothingColor, 70)}28 0%, transparent 65%)`,
        }}
      />
      
      {/* Animated particles when actively signing */}
      {isSigning && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full animate-float-particle"
              style={{
                width: `${3 + (i % 3)}px`,
                height: `${3 + (i % 3)}px`,
                background: `${adjustColor(clothingColor, 100)}50`,
                left: `${15 + i * 10}%`,
                bottom: `${25 + (i % 4) * 12}%`,
                animationDelay: `${i * 0.4}s`,
                animationDuration: `${2.5 + i * 0.4}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Avatar Container */}
      <div className="relative" style={{ width: '150px', marginBottom: '0px' }}>
        
        {/* Upper Arms */}
        <div
          className="absolute transition-all duration-450"
          style={{
            width: '24px',
            height: '55px',
            left: '-10px',
            bottom: '58px',
            background: `linear-gradient(90deg, ${adjustColor(clothingColor, -35)} 0%, ${clothingColor} 50%, ${adjustColor(clothingColor, -25)} 100%)`,
            borderRadius: '35% 35% 0 0',
            transformOrigin: 'top center',
            transform: `rotate(${isSigning ? -18 : -6}deg) translateY(${leftShoulderLift}px)`,
            boxShadow: `inset -3px 0 10px rgba(0,0,0,0.2)`,
          }}
        />
        <div
          className="absolute transition-all duration-450"
          style={{
            width: '24px',
            height: '55px',
            right: '-10px',
            bottom: '58px',
            background: `linear-gradient(270deg, ${adjustColor(clothingColor, -35)} 0%, ${clothingColor} 50%, ${adjustColor(clothingColor, -25)} 100%)`,
            borderRadius: '35% 35% 0 0',
            transformOrigin: 'top center',
            transform: `rotate(${isSigning ? 18 : 6}deg) translateY(${rightShoulderLift}px)`,
            boxShadow: `inset 3px 0 10px rgba(0,0,0,0.2)`,
          }}
        />
        
        {/* Shoulders */}
        <div
          className="absolute transition-all duration-350"
          style={{
            width: '170px',
            height: '35px',
            left: '-10px',
            bottom: '78px',
            background: `linear-gradient(180deg, ${clothingColor} 0%, ${adjustColor(clothingColor, -25)} 100%)`,
            borderRadius: '50% 50% 0 0',
            transform: `translateY(${shoulderOffset}px)`,
            boxShadow: `inset 0 10px 25px rgba(255,255,255,0.12), inset 0 -6px 18px rgba(0,0,0,0.12)`,
          }}
        />
        
        {/* Shoulder Details */}
        <div
          className="absolute transition-all duration-450"
          style={{
            width: '38px',
            height: '48px',
            left: '-18px',
            bottom: '52px',
            background: `linear-gradient(90deg, ${adjustColor(clothingColor, -45)} 0%, ${clothingColor} 100%)`,
            borderRadius: '45% 0 0 45%',
            transform: `translateY(${shoulderOffset + leftShoulderLift}px) rotate(-6deg)`,
          }}
        />
        <div
          className="absolute transition-all duration-450"
          style={{
            width: '38px',
            height: '48px',
            right: '-18px',
            bottom: '52px',
            background: `linear-gradient(270deg, ${adjustColor(clothingColor, -45)} 0%, ${clothingColor} 100%)`,
            borderRadius: '0 45% 45% 0',
            transform: `translateY(${shoulderOffset + rightShoulderLift}px) rotate(6deg)`,
          }}
        />

        {/* Body/Torso */}
        <div
          className="relative rounded-t-[42%] transition-transform duration-350 mx-auto"
          style={{
            width: '130px',
            height: '90px',
            background: `linear-gradient(180deg, ${clothingColor} 0%, ${adjustColor(clothingColor, -35)} 100%)`,
            boxShadow: `
              inset 0 12px 25px rgba(255,255,255,0.08),
              inset 0 -12px 25px rgba(0,0,0,0.12),
              0 5px 15px rgba(0,0,0,0.2)
            `,
            transform: `translateY(${breatheOffset}px)`,
          }}
        >
          {/* Collar/neckline */}
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2"
            style={{
              width: '44px',
              height: '22px',
              background: `linear-gradient(180deg, ${skin.main} 0%, ${skin.shadow} 100%)`,
              borderRadius: '0 0 55% 55%',
            }}
          />
          {/* Collar V-neck detail */}
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2"
            style={{
              width: '60px',
              height: '20px',
              borderLeft: `3.5px solid ${adjustColor(clothingColor, 25)}`,
              borderRight: `3.5px solid ${adjustColor(clothingColor, 25)}`,
              borderRadius: '0 0 12px 12px',
            }}
          />
          {/* Fabric texture */}
          <div
            className="absolute inset-0 opacity-[0.04] rounded-t-[42%]"
            style={{
              backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 2px,
                rgba(255,255,255,0.15) 2px,
                rgba(255,255,255,0.15) 4px
              )`,
            }}
          />
        </div>

        {/* Neck */}
        <div
          className="absolute left-1/2 -translate-x-1/2"
          style={{
            width: '32px',
            height: '26px',
            bottom: '84px',
            background: `linear-gradient(180deg, ${skin.shadow} 0%, ${skin.main} 45%, ${skin.shadow} 100%)`,
            borderRadius: '0 0 38% 38%',
            boxShadow: `inset 0 -4px 8px rgba(0,0,0,0.12)`,
          }}
        />

        {/* Head */}
        <div
          className="absolute left-1/2 -translate-x-1/2 transition-all duration-350"
          style={{
            width: '78px',
            height: '95px',
            bottom: '100px',
            background: `radial-gradient(ellipse at 42% 32%, ${skin.highlight} 0%, ${skin.main} 45%, ${skin.midtone} 75%, ${skin.shadow} 100%)`,
            borderRadius: '44% 44% 46% 46%',
            boxShadow: `
              inset 4px 4px 15px ${skin.highlight}80,
              inset -4px -4px 15px ${skin.shadow}60,
              0 5px 12px rgba(0,0,0,0.2)
            `,
            transform: `translateX(-50%) rotate(${expr.headTilt + microOffset}deg) ${isSigning ? 'translateY(-3px)' : 'translateY(0)'}`,
          }}
        >
          {/* Hair with volume and shine */}
          <div
            className="absolute -top-4 -left-3 -right-3 h-[55px] rounded-t-[48%]"
            style={{
              background: `linear-gradient(180deg, ${adjustColor(hair, 15)} 0%, ${hair} 35%, ${adjustColor(hair, -25)} 100%)`,
              boxShadow: `inset 0 10px 25px rgba(255,255,255,0.12), 0 -3px 6px ${adjustColor(hair, -35)}`,
            }}
          />
          {/* Hair highlight */}
          <div
            className="absolute top-[-10px] left-[18px] w-[28px] h-[22px] rounded-full opacity-25"
            style={{ background: 'white', filter: 'blur(4px)' }}
          />
          {/* Hair sides */}
          <div
            className="absolute top-[8px] -left-1.5 w-4 h-[52px] rounded-l-full"
            style={{ background: `linear-gradient(90deg, ${adjustColor(hair, -18)} 0%, ${hair} 100%)` }}
          />
          <div
            className="absolute top-[8px] -right-1.5 w-4 h-[52px] rounded-r-full"
            style={{ background: `linear-gradient(270deg, ${adjustColor(hair, -18)} 0%, ${hair} 100%)` }}
          />

          {/* Face Features */}
          {/* Eyebrows */}
          <div
            className="absolute transition-all duration-250"
            style={{
              width: '18px',
              height: '4px',
              background: `linear-gradient(90deg, ${adjustColor(hair, 25)} 0%, ${adjustColor(hair, 15)} 100%)`,
              borderRadius: '3px',
              top: `${27 + expr.eyebrow}px`,
              left: '10px',
              transform: `rotate(${-6 - expr.eyebrow * 0.6}deg)`,
            }}
          />
          <div
            className="absolute transition-all duration-250"
            style={{
              width: '18px',
              height: '4px',
              background: `linear-gradient(270deg, ${adjustColor(hair, 25)} 0%, ${adjustColor(hair, 15)} 100%)`,
              borderRadius: '3px',
              top: `${27 + expr.eyebrow}px`,
              right: '10px',
              transform: `rotate(${6 + expr.eyebrow * 0.6}deg)`,
            }}
          />

          {/* Eyes with blinking and expression */}
          <div 
            className="absolute bg-white rounded-full transition-all duration-250 overflow-hidden"
            style={{
              width: `${15 * expr.eyeScale}px`,
              height: `${(isBlinking ? 2 : 11) * expr.eyeScale}px`,
              top: '34px',
              left: '12px',
              boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.18)',
            }}
          >
            {!isBlinking && (
              <div 
                className="absolute bg-[#382820] rounded-full transition-all duration-350"
                style={{
                  width: '10px',
                  height: '10px',
                  top: '0px',
                  left: `${3 + expr.eyeDirection}px`,
                }}
              >
                <div className="absolute top-0.5 left-1.5 w-3 h-3 bg-white rounded-full opacity-85" />
                <div className="absolute bottom-1 right-1 w-1.5 h-1.5 bg-white rounded-full opacity-45" />
              </div>
            )}
          </div>
          <div 
            className="absolute bg-white rounded-full transition-all duration-250 overflow-hidden"
            style={{
              width: `${15 * expr.eyeScale}px`,
              height: `${(isBlinking ? 2 : 11) * expr.eyeScale}px`,
              top: '34px',
              right: '12px',
              boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.18)',
            }}
          >
            {!isBlinking && (
              <div 
                className="absolute bg-[#382820] rounded-full transition-all duration-350"
                style={{
                  width: '10px',
                  height: '10px',
                  top: '0px',
                  right: `${3 - expr.eyeDirection}px`,
                }}
              >
                <div className="absolute top-0.5 right-1.5 w-3 h-3 bg-white rounded-full opacity-85" />
                <div className="absolute bottom-1 left-1 w-1.5 h-1.5 bg-white rounded-full opacity-45" />
              </div>
            )}
          </div>
          
          {/* Eyelashes */}
          {[0, 1, 2].map(i => (
            <React.Fragment key={i}>
              <div
                className="absolute"
                style={{
                  width: '1.5px',
                  height: '3.5px',
                  background: adjustColor(hair, 35),
                  top: '32px',
                  left: `${11 + i * 5}px`,
                  transform: `rotate(${-18 + i * 18}deg)`,
                  transformOrigin: 'bottom center',
                }}
              />
              <div
                className="absolute"
                style={{
                  width: '1.5px',
                  height: '3.5px',
                  background: adjustColor(hair, 35),
                  top: '32px',
                  right: `${11 + i * 5}px`,
                  transform: `rotate(${18 - i * 18}deg)`,
                  transformOrigin: 'bottom center',
                }}
              />
            </React.Fragment>
          ))}

          {/* Nose */}
          <div
            className="absolute left-1/2 -translate-x-1/2"
            style={{
              width: '12px',
              height: '20px',
              top: '44px',
              background: `linear-gradient(180deg, transparent 0%, ${skin.shadow}35 45%, ${skin.shadow}70 100%)`,
              borderRadius: '0 0 50% 50%',
            }}
          />
          {/* Nose highlight */}
          <div
            className="absolute left-1/2 -translate-x-1/2 opacity-35"
            style={{
              width: '5px',
              height: '10px',
              top: '46px',
              background: skin.highlight,
              borderRadius: '50%',
              filter: 'blur(1px)',
            }}
          />

          {/* Mouth with expression */}
          <div
            className="absolute left-1/2 -translate-x-1/2 transition-all duration-250 overflow-hidden"
            style={{
              width: `${26 * expr.mouth}px`,
              height: `${10 * (isSigning ? 1.35 : 1)}px`,
              bottom: '16px',
              background: `linear-gradient(180deg, #c47575 0%, #a85858 50%, #954848 100%)`,
              borderRadius: isSigning 
                ? `${5 + expr.mouthCurve * 2}px ${5 + expr.mouthCurve * 2}px ${12 - expr.mouthCurve}px ${12 - expr.mouthCurve}px`
                : `${30 + expr.mouthCurve * 5}%`,
              boxShadow: 'inset 0 -3px 5px rgba(0,0,0,0.28), inset 0 2px 3px rgba(255,180,180,0.35)',
            }}
          >
            {/* Upper lip highlight */}
            <div
              className="absolute top-0 left-1/2 -translate-x-1/2 w-[45%] h-[2px]"
              style={{ background: 'rgba(255,255,255,0.22)' }}
            />
            {/* Teeth hint when mouth is more open */}
            {expr.mouth > 1.2 && (
              <div
                className="absolute top-[2px] left-1/2 -translate-x-1/2 w-[60%] h-[4px] rounded-b"
                style={{ background: 'rgba(255,255,255,0.7)' }}
              />
            )}
          </div>

          {/* Blush */}
          <div
            className="absolute rounded-full"
            style={{
              width: '18px',
              height: '12px',
              background: `radial-gradient(ellipse, #e8a0a0 0%, transparent 70%)`,
              top: '52px',
              left: '3px',
              opacity: expr.mouth > 1.15 ? 0.55 : 0.28,
              filter: 'blur(5px)',
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              width: '18px',
              height: '12px',
              background: `radial-gradient(ellipse, #e8a0a0 0%, transparent 70%)`,
              top: '52px',
              right: '3px',
              opacity: expr.mouth > 1.15 ? 0.55 : 0.28,
              filter: 'blur(5px)',
            }}
          />
          
          {/* Chin shadow */}
          <div
            className="absolute bottom-[-3px] left-1/2 -translate-x-1/2 w-[55%] h-[10px] rounded-b-full"
            style={{ 
              background: `linear-gradient(180deg, transparent 0%, ${skin.shadow}45 100%)`,
            }}
          />
        </div>
      </div>

      {/* 3D Hands */}
      <Professional3DHand isLeft={true} phase={phase} skinTone={skinTone} size={1} />
      <Professional3DHand isLeft={false} phase={phase} skinTone={skinTone} size={1} />
      
      {/* Animation keyframes */}
      <style>{`
        @keyframes float-particle {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.5; }
          50% { transform: translateY(-25px) scale(1.3); opacity: 0.25; }
        }
        .animate-float-particle {
          animation: float-particle 3.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

// Helper to adjust color brightness
function adjustColor(color, amount) {
  const hex = color.replace('#', '');
  const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
  const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
  const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// Main Sign Language Avatar Component
const SignLanguageAvatarLive = ({
  text = '',
  language = 'asl',
  skinTone = 'medium',
  clothingColor = '#3B82F6',
  hairColor = 'dark-brown',
  size = 'medium',
  showCaption = true,
  onClose,
  onExpand,
  isExpanded = false,
  isListening = false,
  onToggleListening,
  speechSupported = true,
  statusText = '',
  statusColor = '',
}) => {
  const [animationPhase, setAnimationPhase] = useState('rest');
  const [currentWord, setCurrentWord] = useState('');
  const [isSigning, setIsSigning] = useState(false);
  const [expression, setExpression] = useState('neutral');
  const animationRef = useRef(null);
  const wordIndexRef = useRef(0);

  // Enhanced animation phases for realistic signing
  const phases = useMemo(() => [
    'rest', 'wave', 'wave_high', 'point', 'open', 'open_wide', 'fist', 'flat', 'cup', 
    'thumbs_up', 'ok_sign', 'peace', 'love', 'letter_a', 'letter_b', 'letter_c', 'letter_d',
    'clap_in', 'clap_out', 'explain', 'wave', 'open', 'rest'
  ], []);

  // Animate based on incoming text
  useEffect(() => {
    if (!text) {
      setIsSigning(false);
      setAnimationPhase('rest');
      setCurrentWord('');
      setExpression('neutral');
      return;
    }

    const words = text.split(' ').filter(w => w.trim());
    if (words.length === 0) return;

    setIsSigning(true);
    setExpression('attentive');
    wordIndexRef.current = 0;

    if (animationRef.current) {
      clearInterval(animationRef.current);
    }

    animationRef.current = setInterval(() => {
      const wordIdx = wordIndexRef.current;

      if (wordIdx >= words.length) {
        setIsSigning(false);
        setAnimationPhase('rest');
        setCurrentWord('');
        setExpression('neutral');
        clearInterval(animationRef.current);
        return;
      }

      const word = words[wordIdx];
      setCurrentWord(word);

      // Vary expression based on punctuation and word position
      if (word.includes('?')) {
        setExpression('question');
      } else if (word.includes('!')) {
        setExpression('emphasis');
      } else if (wordIdx === 0) {
        setExpression('attentive');
      } else if (wordIdx % 5 === 0) {
        setExpression('happy');
      } else if (wordIdx % 7 === 0) {
        setExpression('explaining');
      } else if (wordIdx % 3 === 0) {
        setExpression('agreeing');
      } else {
        setExpression('focused');
      }

      // Cycle through phases with variety
      const phaseIdx = (wordIdx * 3 + Math.floor(wordIdx / 2)) % (phases.length - 3);
      setAnimationPhase(phases[phaseIdx + 1]);

      wordIndexRef.current++;
    }, 480);

    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
    };
  }, [text, phases]);

  useEffect(() => {
    if (!isSigning && animationPhase !== 'rest') {
      const timeout = setTimeout(() => setAnimationPhase('rest'), 350);
      return () => clearTimeout(timeout);
    }
  }, [isSigning, animationPhase]);

  const sizeClasses = {
    small: 'w-40 h-48',
    medium: 'w-56 h-64',
    large: 'w-72 h-80',
    xlarge: 'w-88 h-[26rem]',
  };

  const config = SIGN_LANGUAGE_CONFIG[language] || SIGN_LANGUAGE_CONFIG.asl;

  return (
    <div
      className={`${isExpanded ? 'w-[26rem] h-[30rem]' : sizeClasses[size]} relative rounded-2xl overflow-hidden shadow-2xl border border-slate-600/40`}
      style={{
        background: 'linear-gradient(150deg, #1e293b 0%, #0f172a 45%, #1a1f2e 100%)',
      }}
      data-testid="sign-language-avatar-live"
    >
      {/* Animated background mesh */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: `
              radial-gradient(circle at 25% 25%, white 1.5px, transparent 1.5px),
              radial-gradient(circle at 75% 75%, white 1px, transparent 1px)
            `,
            backgroundSize: '35px 35px',
          }}
        />
        {/* Gradient orbs */}
        <div 
          className={`absolute -top-24 -left-24 w-48 h-48 rounded-full transition-all duration-1000 ${isSigning ? 'opacity-25' : 'opacity-12'}`}
          style={{ background: 'radial-gradient(circle, #3b82f6 0%, transparent 70%)', filter: 'blur(20px)' }}
        />
        <div 
          className={`absolute -bottom-24 -right-24 w-48 h-48 rounded-full transition-all duration-1000 ${isSigning ? 'opacity-25' : 'opacity-12'}`}
          style={{ background: 'radial-gradient(circle, #8b5cf6 0%, transparent 70%)', filter: 'blur(20px)' }}
        />
        {/* Active signing indicator glow */}
        {isSigning && (
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full animate-pulse"
            style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)' }}
          />
        )}
      </div>

      {/* Header bar */}
      <div className="absolute top-0 left-0 right-0 h-11 bg-black/55 backdrop-blur-lg flex items-center justify-between px-3.5 z-20 border-b border-white/8">
        <div className="flex items-center gap-2.5">
          <div className={`w-2.5 h-2.5 rounded-full transition-all duration-400 ${statusColor || (isSigning ? 'bg-emerald-400 animate-pulse shadow-lg shadow-emerald-400/50' : isListening ? 'bg-amber-400 animate-pulse shadow-amber-400/30' : 'bg-slate-500')}`} />
          <span className="text-[11px] text-white/92 font-semibold tracking-wider uppercase">
            {statusText || (isSigning ? 'Interpreting' : isListening ? 'Listening' : 'Ready')}
          </span>
          {isSigning && (
            <Sparkles className="w-3.5 h-3.5 text-amber-400 animate-pulse" />
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge 
            variant="secondary" 
            className="text-[10px] px-2.5 py-0.5 h-5.5 bg-white/12 text-white/95 border-0 font-medium hover:bg-white/18 transition-colors"
          >
            <Hand className="w-3 h-3 mr-1.5" />
            {config.flag} {config.name}
          </Badge>
          {onToggleListening && speechSupported && (
            <Button
              variant="ghost"
              size="icon"
              className={`h-7 w-7 rounded-full transition-all ${isListening ? 'text-amber-400 bg-amber-400/15 hover:bg-amber-400/25' : 'text-white/70 hover:text-white hover:bg-white/12'}`}
              onClick={onToggleListening}
            >
              {isListening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            </Button>
          )}
          {onExpand && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-white/70 hover:text-white hover:bg-white/12 rounded-full"
              onClick={onExpand}
            >
              {isExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
            </Button>
          )}
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-white/70 hover:text-white hover:bg-red-500/20 rounded-full"
              onClick={onClose}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Avatar container */}
      <div className="absolute inset-0 pt-11 pb-16">
        <Professional3DBody
          skinTone={skinTone}
          clothingColor={clothingColor}
          hairColor={hairColor}
          phase={animationPhase}
          isSigning={isSigning}
          expression={expression}
        />
      </div>

      {/* Caption bar */}
      {showCaption && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/65 backdrop-blur-lg px-4 py-2.5 z-20 border-t border-white/8">
          <div className="text-center min-h-[26px]">
            {currentWord ? (
              <p className="text-white text-sm font-medium truncate animate-fadeIn flex items-center justify-center gap-2">
                <Volume2 className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
                {currentWord}
              </p>
            ) : isListening ? (
              <p className="text-white/50 text-xs italic flex items-center justify-center gap-2">
                <span className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
                Listening for speech...
              </p>
            ) : (
              <p className="text-white/45 text-xs flex items-center justify-center gap-1.5">
                <Hand className="w-3 h-3" />
                Ready to interpret
              </p>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.25s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SignLanguageAvatarLive;
